import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Pencil, Trash2 } from "lucide-react";
import { LeafletMap, type MapDepot, type MapPin } from "@/components/maps/LeafletMap";
import { listDepots } from "@/services/depots";
import { listMappableLeads } from "@/services/routes";
import { listTeamMembers } from "@/services/team";
import {
  assignZoneLeads,
  createCircleZone,
  createPolygonZone,
  createZipSetZone,
  deleteZone,
  listZones,
  type ZoneRow,
} from "@/services/zones";
import { useIsAdmin } from "@/hooks/useCurrentUser";

/**
 * Renders every geocoded, route-eligible lead in the team plus a Zones panel
 * for bulk territory assignment. Three zone shapes are supported (matching
 * the `zones` table from migration 0001 + the create RPCs from 0012):
 *
 *   - polygon   — click vertices on the map; "Finish" closes the ring
 *   - circle    — pick a center on the map; type a radius in meters
 *   - zip_set   — paste a comma/space/newline-separated ZIP list
 *
 * After creation, each zone card shows the matching lead count and an
 * "Assign all to…" dropdown that calls assign_zone_leads (admin-only RPC).
 */
export function LeadsMap() {
  // Zone tooling is part of the elevated-permission bucket — both
  // admin_user and route_manager can create and assign zones.
  const isAdmin = useIsAdmin();

  const qc = useQueryClient();
  const leadsQuery = useQuery({ queryKey: ["leads", "mappable"], queryFn: () => listMappableLeads() });
  const depotsQuery = useQuery({ queryKey: ["depots"], queryFn: listDepots });
  const zonesQuery = useQuery({ queryKey: ["zones"], queryFn: listZones });
  const membersQuery = useQuery({
    // Match the canonical ["team", "members"] key used by Team.tsx and
    // Routes/All.tsx — using ["team-members"] silently fragmented the cache
    // so admin add/remove from Team page didn't refresh the zone-assign
    // dropdown here. Caught by Pass 3 of deep_audit.sql.
    queryKey: ["team", "members"],
    queryFn: listTeamMembers,
    enabled: isAdmin,
  });

  const [tool, setTool] = useState<"none" | "polygon" | "circle">("none");
  const [drawingPoints, setDrawingPoints] = useState<Array<[number, number]>>([]);
  const [circleCenter, setCircleCenter] = useState<[number, number] | null>(null);
  const [circleRadiusM, setCircleRadiusM] = useState(1000);
  const [newZoneName, setNewZoneName] = useState("");
  const [zipInput, setZipInput] = useState("");

  function onMapClick(latlng: [number, number]) {
    if (tool === "polygon") {
      setDrawingPoints((pts) => [...pts, latlng]);
    } else if (tool === "circle") {
      setCircleCenter(latlng);
    }
  }

  const createPolyMut = useMutation({
    mutationFn: () =>
      createPolygonZone({ name: newZoneName.trim() || "Untitled polygon", points: drawingPoints }),
    onSuccess: () => {
      toast.success("Polygon zone saved");
      setTool("none");
      setDrawingPoints([]);
      setNewZoneName("");
      qc.invalidateQueries({ queryKey: ["zones"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const createCircleMut = useMutation({
    mutationFn: () => {
      if (!circleCenter) throw new Error("Pick a center on the map first");
      return createCircleZone({
        name: newZoneName.trim() || "Untitled circle",
        center_lat: circleCenter[0],
        center_lng: circleCenter[1],
        radius_m: circleRadiusM,
      });
    },
    onSuccess: () => {
      toast.success("Circle zone saved");
      setTool("none");
      setCircleCenter(null);
      setNewZoneName("");
      qc.invalidateQueries({ queryKey: ["zones"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const createZipMut = useMutation({
    mutationFn: () => {
      const zips = zipInput
        .split(/[,\s]+/)
        .map((z) => z.trim())
        .filter((z) => /^\d{5}$/.test(z));
      if (zips.length === 0) throw new Error("Enter at least one 5-digit ZIP");
      return createZipSetZone({ name: newZoneName.trim() || `${zips.length} ZIP set`, zip_codes: zips });
    },
    onSuccess: () => {
      toast.success("ZIP-set zone saved");
      setNewZoneName("");
      setZipInput("");
      qc.invalidateQueries({ queryKey: ["zones"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteZone(id),
    onSuccess: () => {
      toast.success("Zone deleted");
      qc.invalidateQueries({ queryKey: ["zones"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const assignMut = useMutation({
    mutationFn: ({ zoneId, assignee }: { zoneId: string; assignee: string | null }) =>
      assignZoneLeads(zoneId, assignee),
    onSuccess: (count) => {
      const noun = count === 1 ? "lead" : "leads";
      toast.success(`Assigned ${count} ${noun}`);
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const leads: MapPin[] = (leadsQuery.data ?? []).map((l) => ({
    id: l.id,
    lat: l.lat,
    lng: l.lng,
    title: (l.full_name ?? `${l.first_name ?? ""} ${l.last_name ?? ""}`.trim()) || "Lead",
    subtitle: [l.address_street, l.address_city, l.address_state, l.address_zip].filter(Boolean).join(", "),
  }));
  const depots: MapDepot[] = (depotsQuery.data ?? [])
    .filter((d) => d.lat != null && d.lng != null)
    .map((d) => ({
      id: d.id,
      lat: d.lat as number,
      lng: d.lng as number,
      label: d.name,
      title: d.name,
      subtitle: [d.address_street, d.address_city, d.address_state, d.address_zip].filter(Boolean).join(", "),
    }));

  // Build polygon overlays for saved zones. Two sources:
  //   - Circle zones: reconstruct ring from center + radius (kept on the
  //     row so we don't round-trip through PostGIS for display).
  //   - Polygon zones: parse the GeoJSON ring from the migration-0016
  //     list_zones_with_geometry RPC. PostGIS emits [lng, lat]; Leaflet
  //     wants [lat, lng], so we swap pairs at parse time.
  // Zip-set zones have no geometry so they skip the overlay (still in the
  // sidebar list with full assign/delete actions).
  const zoneOverlays = useMemo(() => {
    const out: Array<{ id: string; positions: Array<[number, number]>; color: string }> = [];
    for (const z of zonesQuery.data ?? []) {
      if (z.shape === "circle" && z.center_lat != null && z.center_lng != null && z.radius_m) {
        out.push({
          id: z.id,
          color: z.color,
          positions: approxCirclePoints(z.center_lat, z.center_lng, z.radius_m, 48),
        });
      } else if (z.shape === "polygon" && z.geom_geojson) {
        const positions = parseGeoJsonPolygonRing(z.geom_geojson);
        if (positions.length >= 3) {
          out.push({ id: z.id, color: z.color, positions });
        }
      }
    }
    return out;
  }, [zonesQuery.data]);

  if (leadsQuery.isLoading || depotsQuery.isLoading) {
    return <p className="text-sm text-slate-500">Loading map…</p>;
  }
  if (leadsQuery.error || depotsQuery.error) {
    return <p className="text-sm text-rose-700">Failed to load map data.</p>;
  }
  if (leads.length === 0 && depots.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center">
        <p className="text-sm font-medium text-slate-700">No mappable leads or depots yet</p>
        <p className="mt-1 text-xs text-slate-500">
          Import leads, run geocoding, and add a depot in the planner to see them here.
        </p>
      </div>
    );
  }

  const drawHandler = tool === "polygon" || tool === "circle" ? onMapClick : undefined;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-4 text-xs text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-slate-400 ring-1 ring-slate-600" />
          {leads.length.toLocaleString()} routable {leads.length === 1 ? "lead" : "leads"}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-600" />
          {depots.length} {depots.length === 1 ? "depot" : "depots"}
        </span>
        <span className="text-slate-400">
          Pool excludes Sold / DNC / follow-up leads and any lead already on an active route.
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr,320px]">
        <div className="space-y-2">
          {tool !== "none" && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs">
              <span className="text-rose-800">
                {tool === "polygon"
                  ? `Click the map to add vertices (${drawingPoints.length} so far). Need at least 3.`
                  : circleCenter
                    ? `Circle center set at ${circleCenter[0].toFixed(4)}, ${circleCenter[1].toFixed(4)}. Adjust radius below.`
                    : "Click the map to pick a circle center."}
              </span>
              <button
                type="button"
                onClick={() => {
                  setTool("none");
                  setDrawingPoints([]);
                  setCircleCenter(null);
                }}
                className="text-rose-700 underline-offset-2 hover:underline"
              >
                Cancel
              </button>
            </div>
          )}
          <LeafletMap
            leads={leads}
            depots={depots}
            zonePolygons={zoneOverlays}
            drawingPolygon={tool === "polygon" ? drawingPoints : undefined}
            onMapClick={drawHandler}
            height={620}
          />
        </div>

        <ZonesPanel
          isAdmin={isAdmin}
          zones={zonesQuery.data ?? []}
          members={(membersQuery.data ?? []).filter((m) => m.isActive)}
          newZoneName={newZoneName}
          setNewZoneName={setNewZoneName}
          tool={tool}
          setTool={setTool}
          drawingPoints={drawingPoints}
          circleCenter={circleCenter}
          circleRadiusM={circleRadiusM}
          setCircleRadiusM={setCircleRadiusM}
          zipInput={zipInput}
          setZipInput={setZipInput}
          onSavePolygon={() => createPolyMut.mutate()}
          onSaveCircle={() => createCircleMut.mutate()}
          onSaveZipSet={() => createZipMut.mutate()}
          onDelete={(id) => deleteMut.mutate(id)}
          onAssign={(zoneId, assignee) => assignMut.mutate({ zoneId, assignee })}
          saving={createPolyMut.isPending || createCircleMut.isPending || createZipMut.isPending}
          assigning={assignMut.isPending}
        />
      </div>
    </div>
  );
}

// ---------- Zones panel sidebar --------------------------------------------

interface ZonesPanelProps {
  isAdmin: boolean;
  zones: ZoneRow[];
  members: Array<{ id: string; fullName: string }>;
  newZoneName: string;
  setNewZoneName: (s: string) => void;
  tool: "none" | "polygon" | "circle";
  setTool: (t: "none" | "polygon" | "circle") => void;
  drawingPoints: Array<[number, number]>;
  circleCenter: [number, number] | null;
  circleRadiusM: number;
  setCircleRadiusM: (n: number) => void;
  zipInput: string;
  setZipInput: (s: string) => void;
  onSavePolygon: () => void;
  onSaveCircle: () => void;
  onSaveZipSet: () => void;
  onDelete: (id: string) => void;
  onAssign: (zoneId: string, assignee: string | null) => void;
  saving: boolean;
  assigning: boolean;
}

function ZonesPanel(p: ZonesPanelProps) {
  if (!p.isAdmin) {
    return (
      <aside className="rounded-lg border border-slate-200 bg-white p-4 text-xs text-slate-500 shadow-sm">
        Zone tooling is admin-only. Ask an admin to set up territories.
      </aside>
    );
  }
  return (
    <aside className="space-y-4">
      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">New zone</h3>

        <input
          type="text"
          placeholder="Zone name (optional)"
          value={p.newZoneName}
          onChange={(e) => p.setNewZoneName(e.target.value)}
          className="mb-3 block w-full rounded-md border-slate-300 px-3 py-1.5 text-xs shadow-sm focus:border-sky-500 focus:ring-sky-500"
        />

        <div className="space-y-3">
          <ToolBlock
            title="Polygon"
            active={p.tool === "polygon"}
            onActivate={() => p.setTool("polygon")}
            description="Click vertices on the map; need at least 3."
          >
            {p.tool === "polygon" && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[11px] text-slate-500">{p.drawingPoints.length} vertices</span>
                <button
                  type="button"
                  disabled={p.drawingPoints.length < 3 || p.saving}
                  onClick={p.onSavePolygon}
                  className="ml-auto rounded-md bg-sky-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-sky-700 disabled:opacity-40"
                >
                  Save polygon
                </button>
              </div>
            )}
          </ToolBlock>

          <ToolBlock
            title="Circle"
            active={p.tool === "circle"}
            onActivate={() => p.setTool("circle")}
            description="Click a center on the map, then set radius."
          >
            {p.tool === "circle" && (
              <div className="mt-2 space-y-2">
                <label className="block text-[11px] text-slate-500">
                  Radius (meters)
                  <input
                    type="number"
                    min={50}
                    step={50}
                    value={p.circleRadiusM}
                    onChange={(e) => p.setCircleRadiusM(Math.max(50, Number(e.target.value) || 50))}
                    className="mt-1 block w-full rounded-md border-slate-300 px-2 py-1 text-xs shadow-sm focus:border-sky-500 focus:ring-sky-500"
                  />
                </label>
                <button
                  type="button"
                  disabled={!p.circleCenter || p.saving}
                  onClick={p.onSaveCircle}
                  className="w-full rounded-md bg-sky-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-sky-700 disabled:opacity-40"
                >
                  Save circle
                </button>
              </div>
            )}
          </ToolBlock>

          <ToolBlock
            title="ZIP-set"
            active={false}
            onActivate={() => p.setTool("none")}
            description="Paste 5-digit ZIPs separated by commas, spaces, or newlines."
          >
            <textarea
              placeholder="44053, 44054 44055&#10;44056"
              value={p.zipInput}
              onChange={(e) => p.setZipInput(e.target.value)}
              className="mt-2 block h-16 w-full rounded-md border-slate-300 px-2 py-1 text-xs shadow-sm focus:border-sky-500 focus:ring-sky-500"
            />
            <button
              type="button"
              disabled={!p.zipInput.trim() || p.saving}
              onClick={p.onSaveZipSet}
              className="mt-2 w-full rounded-md bg-sky-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-sky-700 disabled:opacity-40"
            >
              Save ZIP-set
            </button>
          </ToolBlock>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Saved zones</h3>
        {p.zones.length === 0 ? (
          <p className="text-xs text-slate-500">No zones yet — create one above to bulk-assign leads.</p>
        ) : (
          <ul className="space-y-2">
            {p.zones.map((z) => (
              <li key={z.id} className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: z.color }} />
                    <span className="truncate font-semibold text-slate-900">{z.name}</span>
                  </div>
                  <span className="shrink-0 rounded-full bg-white px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-500">
                    {z.shape}
                  </span>
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  {z.shape === "zip_set" && z.zip_codes
                    ? `${z.zip_codes.length} ZIPs`
                    : z.shape === "circle" && z.radius_m
                      ? `${(z.radius_m / 1000).toFixed(1)} km radius`
                      : "Polygon"}
                </div>
                <div className="mt-2 flex items-center gap-1.5">
                  <select
                    defaultValue=""
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) return;
                      const assignee = v === "__clear__" ? null : v;
                      const verb = v === "__clear__" ? "clear" : "assign";
                      if (window.confirm(`This will ${verb} the assignee on every lead matching this zone. Continue?`)) {
                        p.onAssign(z.id, assignee);
                      }
                      e.currentTarget.value = "";
                    }}
                    disabled={p.assigning}
                    className="flex-1 rounded-md border-slate-300 px-2 py-1 text-[11px] shadow-sm focus:border-sky-500 focus:ring-sky-500"
                  >
                    <option value="">Assign all to…</option>
                    {p.members.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.fullName}
                      </option>
                    ))}
                    <option value="__clear__">— Clear assignee —</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Delete zone "${z.name}"?`)) p.onDelete(z.id);
                    }}
                    className="rounded-md border border-slate-300 bg-white p-1 text-slate-500 hover:bg-rose-50 hover:text-rose-600"
                    title="Delete zone"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </aside>
  );
}

function ToolBlock({
  title,
  active,
  onActivate,
  description,
  children,
}: {
  title: string;
  active: boolean;
  onActivate: () => void;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={`rounded-md border p-2 ${active ? "border-sky-300 bg-sky-50" : "border-slate-200"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-slate-800">{title}</span>
        {!active && (
          <button
            type="button"
            onClick={onActivate}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-50"
          >
            <Pencil className="h-2.5 w-2.5" />
            Use
          </button>
        )}
      </div>
      <p className="mt-0.5 text-[11px] text-slate-500">{description}</p>
      {children}
    </div>
  );
}

/**
 * Parse PostGIS-emitted GeoJSON for a polygon and return Leaflet-ordered
 * [lat, lng] vertex tuples for the outer ring. PostGIS writes coordinates
 * as [longitude, latitude] (GeoJSON spec); Leaflet wants the opposite. We
 * swap at parse time.
 *
 * Returns an empty array if the input isn't a usable polygon — that lets
 * the caller skip the overlay without exploding.
 */
function parseGeoJsonPolygonRing(geojson: string): Array<[number, number]> {
  try {
    const parsed = JSON.parse(geojson) as { type?: string; coordinates?: number[][][] };
    if (parsed.type !== "Polygon") return [];
    const outer = parsed.coordinates?.[0];
    if (!Array.isArray(outer)) return [];
    return outer
      .filter((pair) => Array.isArray(pair) && pair.length >= 2 && Number.isFinite(pair[0]) && Number.isFinite(pair[1]))
      .map((pair) => [pair[1], pair[0]] as [number, number]);
  } catch {
    return [];
  }
}

/**
 * Approximates a geodesic circle by sampling N evenly-spaced bearings from
 * center at the given radius. Used purely for visual overlay — the canonical
 * geometry on the server is the buffered PostGIS polygon, which is more
 * accurate. Off-by-a-few-meters here is invisible at typical map zooms.
 */
function approxCirclePoints(lat: number, lng: number, radiusM: number, sides: number): Array<[number, number]> {
  const EARTH_R = 6371000;
  const out: Array<[number, number]> = [];
  for (let i = 0; i < sides; i++) {
    const bearing = (2 * Math.PI * i) / sides;
    const dx = (radiusM * Math.sin(bearing)) / EARTH_R;
    const dy = (radiusM * Math.cos(bearing)) / EARTH_R;
    const newLat = lat + (dy * 180) / Math.PI;
    const newLng = lng + ((dx * 180) / Math.PI) / Math.cos((lat * Math.PI) / 180);
    out.push([newLat, newLng]);
  }
  // close the ring
  out.push(out[0]);
  return out;
}

